import { MouseEvent, ReactElement, useMemo } from 'react';

import { rem } from '@vimeo/bokeh';
import { Archive, PinOffFilled, PinOn, Unarchive, CircleCheck } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Callable } from '@vimeo/core/types/generic';

import { T_APPROVE, T_ARCHIVE, T_PIN_TO_TOP, T_UNARCHIVE, T_UNPIN } from '../../../../translations';
import { createDomName } from '../../../../utils/dom/identifying';
import { BokehIconButton, BokehTooltip } from '../../../bokeh';

interface IActionButtonDescriber {
  id: string;
  icon: ReactElement;
  tooltipLabel: string;
  onClick: Callable | ((event: MouseEvent) => void);
}

interface IQuestionItemActionsProps extends IBaseDomElementProps {
  isPending: boolean;
  isArchived: boolean;
  isPinned: boolean;
  hasActiveMenu?: boolean;
  onQuestionArchiveClicked: Callable;
  onQuestionUnArchiveClicked: Callable;
  onQuestionApproveClicked: Callable;
  onQuestionUnPinClicked: (event: MouseEvent) => void;
  onQuestionPinClicked: (event: MouseEvent) => void;
}

export function QuestionItemActions({
  className,
  isPending,
  isArchived,
  isPinned,
  hasActiveMenu = false,
  onQuestionArchiveClicked,
  onQuestionUnArchiveClicked,
  onQuestionApproveClicked,
  onQuestionUnPinClicked,
  onQuestionPinClicked,
}: IQuestionItemActionsProps) {
  const buttons: Array<IActionButtonDescriber> = useMemo(() => {
    if (isPending) {
      return [
        {
          id: 'archive',
          icon: <Archive />,
          tooltipLabel: T_ARCHIVE,
          onClick: onQuestionArchiveClicked,
        },
        {
          id: 'approve',
          icon: <CircleCheck />,
          tooltipLabel: T_APPROVE,
          onClick: onQuestionApproveClicked,
        },
      ];
    }

    if (isArchived) {
      return [
        {
          id: 'unarchive',
          icon: <Unarchive />,
          tooltipLabel: T_UNARCHIVE,
          onClick: onQuestionUnArchiveClicked,
        },
      ];
    }

    const activeButtons: Array<IActionButtonDescriber> = [
      {
        id: isPinned ? 'unpin' : 'pin',
        icon: isPinned ? <PinOffFilled /> : <PinOn />,
        tooltipLabel: isPinned ? T_UNPIN : T_PIN_TO_TOP,
        onClick: isPinned ? onQuestionUnPinClicked : onQuestionPinClicked,
      },
    ];

    if (!hasActiveMenu) {
      activeButtons.push({
        id: 'archive',
        icon: <Archive />,
        tooltipLabel: T_ARCHIVE,
        onClick: onQuestionArchiveClicked,
      });
    }

    return activeButtons;
  }, [
    isArchived,
    isPending,
    isPinned,
    hasActiveMenu,
    onQuestionApproveClicked,
    onQuestionArchiveClicked,
    onQuestionPinClicked,
    onQuestionUnArchiveClicked,
    onQuestionUnPinClicked,
  ]);

  return (
    <>
      {buttons.map(({ id, icon, tooltipLabel, onClick }) => (
        <BokehTooltip
          key={id}
          placement={'top'}
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: ['top-end'],
              },
            },
          ]}
          label={tooltipLabel}
        >
          <BokehIconButton
            className={createDomName(className, id)}
            size={'xs'}
            icon={icon}
            padding={'0!important'}
            width={rem(24)}
            variant={'secondary'}
            onClick={onClick}
          />
        </BokehTooltip>
      ))}
    </>
  );
}
